import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Collapse from "@mui/material/Collapse"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Typography from "@mui/material/Typography"
import {
  AddOutlined,
  CalendarTodayOutlined,
  DeleteOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  MoreVertOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useCallback, useMemo, useRef, useState } from "react"

import { Button } from "@/components/common/Button"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import type { CompanyCertification, CompanyCertificationType, Location, SignedUrl } from "@/graphql/codegen/graphql"
import BulkActions from "@/screens/Companies/edit/components/certification/components/BulkActions"
import CertificationTable from "@/screens/Companies/edit/components/certification/components/tables/CertificationTable"
import type { GroupedByCertificationType } from "@/screens/Companies/edit/components/certification/types"
import { CertificationDialogType } from "@/screens/Companies/edit/components/certification/types"
import {
  certificationStatusByGroupedLocation,
  extractSelectedCertificationIds,
  getCertificationLabel,
  transformCertificationTableData,
} from "@/screens/Companies/edit/components/certification/utils"
import { CertificationStatusMap } from "@/screens/Companies/edit/components/certification/utils/certificationStatusMap"

interface CertificationItemProps {
  locations: Location[]
  groupedCertification: GroupedByCertificationType
  selectedCertifications: Record<string, boolean>
  setSelectedCertifications: (selection: Record<string, boolean>) => void
  setOpenedDialog: (dialog: CertificationDialogType) => void
  setPreSelectedCertificationType: (certificationType: CompanyCertificationType) => void
  setPreSelectedLocations: (locationIds: string[]) => void
  setSelectedCertification: (certification: CompanyCertification) => void
  handleDeleteAllCertifications: () => void
  handleDeleteSelectedCertifications: (certificationIds: string[]) => void
  fileList: SignedUrl[]
}

const CertificationCard: FC<CertificationItemProps> = ({
  locations,
  groupedCertification,
  selectedCertifications,
  setSelectedCertifications,
  setOpenedDialog,
  setPreSelectedCertificationType,
  setPreSelectedLocations,
  setSelectedCertification,
  handleDeleteAllCertifications,
  handleDeleteSelectedCertifications,
  fileList,
}) => {
  const [expanded, setExpanded] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [showAllLocations, setShowAllLocations] = useState(false)

  const selectedKeys = Object.keys(selectedCertifications)

  const isDeleteDisabled = selectedKeys.length === 0

  const data = useMemo(
    () => transformCertificationTableData(groupedCertification, locations, showAllLocations),
    [groupedCertification, locations, showAllLocations]
  )

  const anchorElRef = useRef<HTMLButtonElement>(null)

  const handleToggleMenuOpen = () => setMenuOpen(!menuOpen)

  const handleClickHistoricCertificates = () => {
    setPreSelectedCertificationType(groupedCertification.certificationType)
    setOpenedDialog(CertificationDialogType.HISTORIC_CERTIFICATES)
    handleToggleMenuOpen()
  }

  const handleClickCreateCertificate = () => {
    setPreSelectedCertificationType(groupedCertification.certificationType)
    setOpenedDialog(CertificationDialogType.ADD_CERTIFICATION)
  }

  const handleClickBulkCreate = () => {
    setPreSelectedLocations(selectedKeys)
    setPreSelectedCertificationType(groupedCertification.certificationType)
    setOpenedDialog(CertificationDialogType.ADD_CERTIFICATION)
  }

  const handleClickCertificateDeleteAll = () => {
    setPreSelectedCertificationType(groupedCertification.certificationType)
    handleDeleteAllCertifications()
    handleToggleMenuOpen()
  }

  const handleDeleteSelectedCertificationsClick = () => {
    setPreSelectedCertificationType(groupedCertification.certificationType)
    handleDeleteSelectedCertifications(extractSelectedCertificationIds(groupedCertification, selectedKeys))
  }

  const handleClickEdit = useCallback(
    (certification: CompanyCertification) => {
      setSelectedCertification(certification)
      setOpenedDialog(CertificationDialogType.EDIT_CERTIFICATION)
    },
    [setSelectedCertification, setOpenedDialog]
  )

  return (
    <Box className='my-2 rounded-md border border-solid border-gray-400'>
      <Box className='flex w-full justify-between p-6'>
        <Box className='flex flex-col justify-between'>
          <Typography variant='h6' className='text-gray-900'>
            {getCertificationLabel(groupedCertification.certificationType)}
          </Typography>
          <Box className='flex w-full flex-wrap items-center gap-4 overflow-hidden'>
            <Typography variant='body2' className='text-gray-900'>
              Locations:
            </Typography>

            {groupedCertification.locations.map((groupedByLocation) => (
              <Chip
                key={groupedCertification.certificationType + "-" + groupedByLocation.location.name}
                label={groupedByLocation.location.name}
                avatar={
                  <StatusIndicator
                    status={certificationStatusByGroupedLocation(groupedByLocation)}
                    map={CertificationStatusMap}
                    size='medium'
                  />
                }
                className='rounded bg-gray-200 pl-5'
              />
            ))}
          </Box>
        </Box>

        <Box className='flex flex-col justify-between'>
          <IconButton onClick={handleToggleMenuOpen} ref={anchorElRef}>
            <MoreVertOutlined />
          </IconButton>
          <Menu
            anchorEl={anchorElRef.current}
            open={menuOpen}
            onClose={handleToggleMenuOpen}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem onClick={handleClickCertificateDeleteAll}>
              <ListItemIcon>
                <DeleteOutlined className='text-gray-600' />
              </ListItemIcon>
              <ListItemText
                primary={`Delete all ${getCertificationLabel(groupedCertification.certificationType)} Certificates`}
                className='text-gray-800'
              />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleClickHistoricCertificates}>
              <ListItemIcon>
                <CalendarTodayOutlined className='text-gray-600' />
              </ListItemIcon>
              <ListItemText primary='Historic Certificates' className='text-gray-800' />
            </MenuItem>
          </Menu>

          <IconButton onClick={() => setExpanded(!expanded)}>
            {expanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} timeout='auto' unmountOnExit>
        <Divider className='my-4' />

        <Box className='flex flex-col justify-between p-6'>
          <BulkActions
            isDeleteDisabled={isDeleteDisabled}
            onClickBulkDelete={handleDeleteSelectedCertificationsClick}
            onClickBulkCreate={handleClickBulkCreate}
          />

          <CertificationTable
            data={data}
            fileList={fileList}
            onEdit={handleClickEdit}
            selected={selectedCertifications}
            setSelected={setSelectedCertifications}
          />

          <Box className='mt-2 flex w-full justify-between'>
            <Button appearance='text' onClick={handleClickCreateCertificate}>
              <AddOutlined />
              Add Certificate
            </Button>

            <Button
              disabled={locations.length === groupedCertification.locations.length}
              appearance='text'
              onClick={() => setShowAllLocations((prev) => !prev)}
            >
              {showAllLocations ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
              {showAllLocations ? "Hide" : "Show All"}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}

export default CertificationCard
